import { useI18n } from './useI18n';
import { RequestStatus, ShiftPreference } from '@shared/types';
import type { RequestTypeKey } from '@shared/types';

/**
 * Hook to get translations related to requests.
 * Provides typed functions to access translations for status, types, etc.
 */
export interface UseRequestTranslationsReturn {
    /**
     * Get status label
     */
    getStatusLabel: (status: RequestStatus) => string;
    /**
     * Get request type label
     */
    getRequestTypeLabel: (type: RequestTypeKey) => string;
    /**
     * Get shift preference label
     */
    getShiftPreferenceLabel: (preference: ShiftPreference) => string;
    /**
     * Get tab label
     */
    getTabLabel: (tab: 'drafts' | 'current' | 'past') => string;
    /**
     * Resubmit dialog
     */
    resubmitDialog: {
        title: string;
        text: string;
        confirm: string;
    };
}

/**
 * RequestStatus (API values) to translation keys mapping
 */
const STATUS_KEY_MAP: Record<RequestStatus, string> = {
    [RequestStatus.DRAFT]: 'draft',
    [RequestStatus.SUBMITTED]: 'delivered',
    [RequestStatus.APPROVED]: 'approved',
    [RequestStatus.REJECTED]: 'declined',
    [RequestStatus.CANCELLED]: 'cancelled',
    [RequestStatus.MODIFIED]: 'modified',
};

/**
 * RequestTypeKey to translation keys mapping
 */
const REQUEST_TYPE_KEY_MAP: Record<RequestTypeKey, string> = {
    VACATION: 'vacation',
    PAID_LEAVE: 'paidLeave',
    SHIFT_PREFERENCE: 'shiftPreference',
} as const;

/**
 * ShiftPreference to translation keys mapping
 */
const SHIFT_PREFERENCE_KEY_MAP: Record<ShiftPreference, string> = {
    [ShiftPreference.MORNING]: 'morning',
    [ShiftPreference.AFTERNOON]: 'afternoon',
    [ShiftPreference.NIGHT]: 'night',
    [ShiftPreference.WEEKEND]: 'weekend',
    [ShiftPreference.OTHER]: 'other',
};

/**
 * Hook for request translations
 */
export function useRequestTranslations(): UseRequestTranslationsReturn {
    const { t } = useI18n();

    const getStatusLabel = (status: RequestStatus): string => {
        return t(`requests.status.${STATUS_KEY_MAP[status]}`);
    };

    const getRequestTypeLabel = (type: RequestTypeKey): string => {
        return t(`requests.types.${REQUEST_TYPE_KEY_MAP[type]}`);
    };

    const getShiftPreferenceLabel = (preference: ShiftPreference): string => {
        return t(
            `requests.shiftPreferences.${SHIFT_PREFERENCE_KEY_MAP[preference]}`,
        );
    };

    const getTabLabel = (tab: 'drafts' | 'current' | 'past'): string => {
        return t(`requests.tabs.${tab}`);
    };

    return {
        getStatusLabel,
        getRequestTypeLabel,
        getShiftPreferenceLabel,
        getTabLabel,
        resubmitDialog: {
            title: t('requests.resubmitDialog.title'),
            text: t('requests.resubmitDialog.text'),
            confirm: t('requests.resubmitDialog.confirm'),
        },
    };
}
